import type {ReactNode, SyntheticEvent} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {
  type SelectorItemT,
  type SelectorOptionT,
} from '@freckle/educator-entities/ts/common/logic/generic-selector'

type GenericSelectorProps<T> = {
  onChangeSelection: (selection: T) => void
  selection: T | undefined | null
  collection: Array<SelectorItemT<T>>
  required?: boolean
  disabled?: boolean
  size?: number
  className?: string
  id?: string
  dataTest?: string
}

export function GenericSelector<T>(props: GenericSelectorProps<T>) {
  function onChange(e: SyntheticEvent) {
    // Need this refinement to access `e.target.value`
    if (e.target instanceof HTMLSelectElement) {
      props.onChangeSelection(JSON.parse(e.target.value))
    }
  }

  function renderOption(option: SelectorOptionT<T>): ReactNode {
    const key = JSON.stringify(option.key)
    const {dataTest, disabled, label} = option
    const dataTestAttr = dataTest === undefined ? {} : {'data-test': dataTest}
    return (
      <option key={key} disabled={disabled} value={key} {...dataTestAttr}>
        {label}
      </option>
    )
  }

  const options = props.collection.map(item => {
    switch (item.tag) {
      case 'group':
        return (
          <optgroup key={item.key} label={item.label}>
            {item.options.map(renderOption)}
          </optgroup>
        )
      case 'option':
        return renderOption(item)
      default:
        return exhaustive(item, 'SelectorItemT')
    }
  })

  const size =
    props.size === undefined || props.disabled === true
      ? undefined
      : Math.min(props.size, options.length)

  const hasNullKey = props.collection.some(item => {
    switch (item.tag) {
      case 'group':
        return item.options.some(option => option.key === null)
      case 'option':
        return item.key === null
      default:
        return exhaustive(item, 'SelectorItemT')
    }
  })

  // A value of undefined will show the select box with no selected option (nothing highlighted).
  // In the case of an item with a `null` key, the logic to use `undefined` should be bypassed
  // because a selection of `null` should indicate that the item in question is selected.
  const value =
    !hasNullKey && (props.selection === null || props.selection === undefined)
      ? undefined
      : JSON.stringify(props.selection)

  return (
    <select
      id={props.id}
      className={`form-control ${props.className ?? ''}`}
      onChange={onChange}
      required={props.required}
      disabled={props.disabled}
      size={size}
      value={value}
      data-test={props.dataTest}
    >
      {options}
    </select>
  )
}
